import produce from "immer";
import moment, { Moment } from "moment";
import { APP_ROUTES, PRODUCT_TYPES, SERVICE_TYPES, SERVICE_TYPES_KEYS } from "../../../config/constants";
import { isEmptyOrNull, isUndefined } from "../../../utils/DataUtils";
import { foodSplitRegex, POS_ZONE_STATE_STATUS, PRE_SELECTED_FOOD_SUB_TYPES } from "./constants";
import { STORE_SERVICE_LABELS } from "components/common/App/app-constant";

interface BREADCRUMB_LINKS {
  name: string;
  to: string;
  isCurrentRoute?: boolean;
}

// handle food type selection on service Slot
export const handleProductType = (configTabs: any, tabIndex: number, selectedTypes: any, checked: boolean) => {
  return produce(configTabs, (draft: any) => {
    const tab = draft[tabIndex];

    // Handle special toggle types (busyOrderEnabled, offlineOrderEnabled)
    if (selectedTypes === PRODUCT_TYPES.BUSY_ORDER_ENABLED) {
      tab.busyOrderEnabled = checked;
      return;
    }
    
    if (selectedTypes === PRODUCT_TYPES.OFFLINE_ORDER_ENABLED) {
      tab.offlineOrderEnabled = checked;
      return;
    }

    // Handle food types selection/deselection
    const existingFoodTypes = isEmptyOrNull(tab.foodTypes) ? [] : tab.foodTypes.split(foodSplitRegex);

    if (checked) {
      // Add all sub food types when FOOD is selected
      if (selectedTypes === PRODUCT_TYPES.FOOD) {
        tab.foodSubType = PRE_SELECTED_FOOD_SUB_TYPES;
      }
      // Add the selected type if not already present
      if (!existingFoodTypes.includes(selectedTypes)) {
        existingFoodTypes.push(selectedTypes);
      }
    } else {
      // Clear sub food types when FOOD is deselected
      if (selectedTypes === PRODUCT_TYPES.FOOD) {
        tab.foodSubType = "";
      }
      // Remove the deselected type
      const typeIndex = existingFoodTypes.indexOf(selectedTypes);
      if (typeIndex > -1) {
        existingFoodTypes.splice(typeIndex, 1);
      }
    }

    tab.foodTypes = existingFoodTypes.join(foodSplitRegex);
  });
};

/**
 * Method to update selected food sub items
 * @param {object} event
 */
export const handleFoodSubType = (configTabs: any, tabIndex: number, selectedFoodSubType: any) => {
  let newTabs = produce(configTabs, (draft: any) => {
    draft[tabIndex].foodSubType = selectedFoodSubType;
  });
  return newTabs;
};

// Send all exclude food types to server
export const productTypesToBeSent = (foodType: any, foodSubType: any, isPosTypePharmacy: boolean) => {
  if (isPosTypePharmacy) {
    // we will nothing exclude in case of pharmacy
    return {
      foodTypes: ["FOOD_DRY", "FOOD_FRESH", "FOOD_FROZEN"],
      excluded: true,
    };
  } else {
    let productType: Array<any> = [];
    const selectedFoodType = isEmptyOrNull(foodType) ? [] : foodType.split(foodSplitRegex);

    if (selectedFoodType.includes(PRODUCT_TYPES.FOOD) && foodSubType.length) {
      productType = [...foodSubType.split(foodSplitRegex)];
    }

    if (selectedFoodType.includes(PRODUCT_TYPES.NON_FOOD)) {
      productType = [...productType, ...["NON_FOOD"]];
    }
    return productType.length > 0
      ? {
          foodTypes: productType,
          // added excluded:true as per new contract in backend
          excluded: true,
        }
      : {};
  }
};

export function getPOSBreadCum(service: SERVICE_TYPES_KEYS) {
  const BREADCRUMB_LINKS: Array<BREADCRUMB_LINKS> = [
    { name: "POS List", to: APP_ROUTES.POS_LIST },
    { name: STORE_SERVICE_LABELS.POS_DETAILS, to: `${APP_ROUTES.CREATE_POS}${APP_ROUTES.BASIC_DETAILS}` },
    {
      name: "Service & Slots",
      to: "#",
    },
  ];
  switch (service) {
    case SERVICE_TYPES.STANDARD:
      BREADCRUMB_LINKS.push({ name: "Standard", to: `${APP_ROUTES.SERVICE_AND_SLOTS}${APP_ROUTES.STANDARD}`, isCurrentRoute: true });
      break;
    case SERVICE_TYPES.EXPRESS:
      BREADCRUMB_LINKS.push({ name: "Express", to: `${APP_ROUTES.SERVICE_AND_SLOTS}${APP_ROUTES.EXPRESS}`, isCurrentRoute: true });
      break;
    case SERVICE_TYPES.CLICK_N_COLLECT:
      BREADCRUMB_LINKS.push({ name: "Click & Collect", to: `${APP_ROUTES.SERVICE_AND_SLOTS}${APP_ROUTES.CLICK_N_COLLECT}`, isCurrentRoute: true });
      break;
    case SERVICE_TYPES.DEFAULT:
      BREADCRUMB_LINKS.push({ name: "Default", to: `${APP_ROUTES.SERVICE_AND_SLOTS}${APP_ROUTES.DEFAULT}`, isCurrentRoute: true });
      break;
  }
  return BREADCRUMB_LINKS;
}

export function isNonFoodType(configTab: any): boolean {
  let alreadySelectedFoodItems = configTab.foodTypes ? configTab.foodTypes.split(foodSplitRegex) : [];

  return !alreadySelectedFoodItems.includes(PRODUCT_TYPES.FOOD);
}

export const getFoodTypesFromFoodSubTypes = (selectedProductTypes: Array<string>) => {
  let foodTypesArr = [];

  if (selectedProductTypes?.some((item) => item === "FOOD_DRY" || item === "FOOD_FRESH" || item === "FOOD_FROZEN")) {
    foodTypesArr.push(PRODUCT_TYPES.FOOD);
  }

  if (selectedProductTypes?.includes("NON_FOOD")) {
    foodTypesArr.push(PRODUCT_TYPES.NON_FOOD);
  }

  return foodTypesArr.join(foodSplitRegex);
};

export type OrderCutoff = {
  bufferTimeHours: number;
  bufferTimeMinutes: number;
  startTime: Moment | string;
};

export const calculateOrderCutOff = (slot: OrderCutoff) => {
  let orderCutOff = "";
  const { bufferTimeHours, bufferTimeMinutes, startTime } = slot;
  if (startTime && moment(startTime).isValid()) {
    const totalBufferTimeInMinutes = bufferTimeHours * 60 + bufferTimeMinutes;
    orderCutOff = moment(startTime).subtract(totalBufferTimeInMinutes, "m").format("HH:mm");
  }

  return orderCutOff || "";
};

export const handleBusyTimeFieldsChange = (configTabs: any, tabIndex: number, event: any, name: string) => {
  let newTabs = produce(configTabs, (draft: any) => {
    if (!isUndefined(tabIndex) && !isUndefined(name) && !isUndefined(event?.target?.value)) {
      draft[tabIndex][name] = event?.target?.value;
    }
    return draft;
  });
  return newTabs;
};

export const handleConfigurationStateSwitch = (configTabs: any, tabIndex: number, event: any) => {
  let newTabs = produce(configTabs, (draft: any) => {
    if (!isUndefined(tabIndex) && !isUndefined(event?.target?.checked)) {
      draft[tabIndex].stateEdit = event?.target?.checked;
      draft[tabIndex].busyDurationHour = "";
      draft[tabIndex].busyDurationMinutes = "";
    }
    return draft;
  });
  return newTabs;
};

export const handleZoneStateSwitchUtils = (configTabs: any, tabIndex: number, event: any) => {
  let newTabs = produce(configTabs, (draft: any) => {
    if (!isUndefined(tabIndex) && !isUndefined(event?.target?.checked)) {
      let state: string = POS_ZONE_STATE_STATUS.AVAILABLE;
      if (!event?.target?.checked) {
        state = POS_ZONE_STATE_STATUS.BUSY;
      }
      draft[tabIndex].state = state;
      draft[tabIndex].stateEdit = true;
      draft[tabIndex].busyEndTime = "";
      draft[tabIndex].busyDurationHour = "";
      draft[tabIndex].busyDurationMinutes = "";
    }
    return draft;
  });
  return newTabs;
};
